"use client";
import { Link } from "@/components/CustomLink";
import { ROUTES } from "@/@core/constants/routes";
import { Button } from "@/components/ui/button";
import ArrowDownRightIcon from "@heroicons/react/24/solid/ArrowDownRightIcon";
import MapPinIcon from "@heroicons/react/24/solid/MapPinIcon";
import BlogLinks from "@/components/views/blog/details/Links";
import { useTranslation } from "@/hooks/translationContext";
import { usePlausible } from "next-plausible";
import {
  CLICK_QUOTE_EVENT_SECTIONS,
  PLAUSIBLE_GOAL_KEYS,
} from "@/@core/constants/plausible";
import { BlogListType } from "@/@core/enums/blog.enum";
import SearchBlogInput from "@/components/views/blog/details/search-input/SearchBlogInput";
import { ChatBubbleLeftIcon } from "@heroicons/react/24/solid";
import { useViewMode } from "@/context/ViewModeContext";
import { useIsAskQuestionEnabled } from "@/hooks/useIsAskQuestionEnabled";

export default function BlogDetailLeftSide({ blog }: { blog: Blog }) {
  const { t } = useTranslation();
  const plausible = usePlausible();
  const { toggleViewMode, viewMode } = useViewMode();
  const isAskQuestionEnabled = useIsAskQuestionEnabled();

  const viewButtonTitle = t("blog-detail-venue-view-button", {
    defaultValue: "İşletmeleri Gör",
  });

  const sendPlausibleEvent = () => {
    plausible(PLAUSIBLE_GOAL_KEYS.CLICK_QUOTE_EVENT, {
      props: {
        section: CLICK_QUOTE_EVENT_SECTIONS.BLOG_DETAIL,
      },
    });
  };

  const handleViewClick = () => {
    const targetId =
      blog.list_type === BlogListType.FIRST_VENUE
        ? "blog-detail"
        : "related-venues";
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleClickCommentButton = () => {
    const element = document.getElementById("comment-form");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  return (
    <aside
      id="left-slide"
      className="sticky top-[120px] flex-1 hidden xl:inline-flex w-full flex-col gap-1.25 max-h-[calc(100vh-140px)]"
    >
      <div className="flex flex-col items-start p-4 gap-2 justify-center flex-shrink-0">
        <SearchBlogInput />

        {isAskQuestionEnabled && (
          <div className="w-full">
            <Link
              onClick={() => sendPlausibleEvent()}
              aria-label={`${t("quote-button-title")}`}
              href={ROUTES.CONTACT_WITH_VENUES}
            >
              <Button
                className="w-full rounded-full text-sm font-bold py-3 px-4 justify-center"
                variant="gradient"
                size="lg"
              >
                {t("quote-button-title")}
              </Button>
            </Link>
          </div>
        )}
        {/**/}
        <div className="flex flex-col w-full gap-2">
          <Button
            onClick={() => handleViewClick()}
            className="w-full flex justify-center rounded-full !px-4 p-2 h-[36px] self-stretch relative"
            variant="primary"
            size="md"
          >
            <ArrowDownRightIcon className="size-4" />
            <span className="text-center flex-1">
              {blog.list_type == BlogListType.FIRST_TEXT
                ? viewButtonTitle
                : "Yazıyı Oku"}
            </span>
          </Button>

          <Button
            onClick={toggleViewMode}
            className="flex w-full h-[36px] justify-center rounded-full !px-4 p-2 self-stretch relative"
            variant="primary"
            size="md"
          >
            <MapPinIcon className="size-4" />
            <span className="flex-1 text-center">Harita Görünümü</span>
          </Button>
          <Button
            onClick={handleClickCommentButton}
            variant="accent"
            size="md"
            className="w-full flex items-center justify-center gap-2 rounded-full px-4 py-2"
          >
            <ChatBubbleLeftIcon className="size-4" />
            <span className="flex-1 text-center">Yorum Yap/Soru Sor</span>
          </Button>
        </div>
      </div>

      <div className="flex flex-col flex-1 min-h-0 w-full left-0 items-start gap-4 px-4 p-2 border-b border-gray-secondary">
        <div className="w-full px-4 py-2 bg-gray-secondary rounded-lg text-primary font-bold text-sm flex-shrink-0">
          Bu Yazıda Neler Var?
        </div>
        <div className="gap-2 flex-1 overflow-y-auto">
          <BlogLinks headings={blog?.headings} description={blog?.descriptions?.[0]?.description} />
        </div>
      </div>
    </aside>
  );
}